import { logAsyncError } from '@Utils/Decorators';
import store from '@Store';
import History from '@Services/History';
import HistoryResource from '@Services/HistoryResource';
import HistoryHelper from '@Services/HistoryHelper';
import Search from '@Services/Search';
import { updateDialog, closeDialog } from '@Containers/Dialogs/DialogsActions';
import {
  HISTORY_LOADING,
  TOGGLE_SELECT_ALL_ON_PAGE,
  RECEIVED_RECENT_HISTORY,
  RECEIVED_SAVED_HISTORY,
  HANDLE_VIEW_SAVE_MODAL,
  HANDLE_NAME_CHANGE,
  ON_SUBMIT_SAVE,
  ON_SUBMIT_COMPLETE,
  HANDLE_VIEW_DELETE_MODAL,
  ON_DELETE,
  ON_DELETE_COMPLETE,
  ON_SAVE_UNLOAD,
  ON_PAGE_CHANGE,
  typeOfHistorySaveDialog,
  typeOfHistoryConfirmationDialog,
  HANDLE_CHECKBOX_CHANGE,
  HANDLE_SEARCH_INPUT,
  historyReducerKey,
} from './Constants';
import { trackHistoryMiniSearchResultsUpdated } from './analytics';

export const onSubmitSave = () => ({
  type: ON_SUBMIT_SAVE,
});
const getHistoryItemKey = (item = {}) => `${item?.id || ''}|${item?.databaseId || ''}|${item?.resultName || ''}`;

export const onSubmitSuccess = (result, isRename, newName, historyItem) => ({
  type: ON_SUBMIT_COMPLETE,
  payload: result || {},
  responseType: 'success',
  isRename,
  newName,
  renameItemKey: isRename ? getHistoryItemKey(historyItem) : undefined,
});
export const onSubmitFail = (result) => {
  const response = {
    ...result,
    message: 'Error saving this search',
    component: 'History',
  };
  return {
    type: ON_SUBMIT_COMPLETE,
    payload: response || {},
    responseType: 'fail',
  };
};
export const onDeleteClick = () => ({
  type: ON_DELETE,
});
export const onDeleteSuccess = (result) => ({
  type: ON_DELETE_COMPLETE,
  payload: result || {},
  responseType: 'success',
});
export const onDeleteFail = (result) => {
  const response = {
    ...result,
    message: 'Error deleting this search',
    component: 'History',
  };
  return {
    type: ON_DELETE_COMPLETE,
    payload: response || {},
    responseType: 'fail',
  };
};
export const onSaveUnLoad = () => ({
  type: ON_SAVE_UNLOAD,
});
export const receivedRecentHistoryAction = (history, historyTypes, totalElements = 0, totalPages = 0) => {
  HistoryHelper.formatHistoryData(history, historyTypes);
  HistoryHelper.updateItemsComments(history);
  const databaseNameById = new Map(
    (HistoryHelper.splitHistoryByDBs(history) || []).map((group) => [`${group?.databaseId ?? ''}`, group?.databaseName || ''])
  );
  const recentHistory = (history || []).map((item) => ({
    ...item,
    databaseName: item?.databaseName || databaseNameById.get(`${item?.databaseId ?? ''}`) || '',
  }));
  return {
    type: RECEIVED_RECENT_HISTORY,
    recentHistory,
    historyTypes,
    totalElements,
    totalPages,
  };
};
export const setHistoryLoading = (page) => ({ type: HISTORY_LOADING, page });

async function fetchAllBackendRecentHistory(size, sort) {
  const firstPage = await HistoryResource.fetchRecentHistory({ page: 0, size, sort });
  const remainingPageNumbers = Array.from({ length: Math.max((firstPage?.totalPages ?? 0) - 1, 0) }, (_, index) => index + 1);
  const remainingPages = await Promise.all(
    remainingPageNumbers.map((pageNumber) => HistoryResource.fetchRecentHistory({ page: pageNumber, size, sort }))
  );
  return [firstPage?.content || [], ...remainingPages.map((response) => response?.content || [])].flat();
}

async function getCombinedRecentHistory(size, sort) {
  const cachedHistory = HistoryHelper.getRecentHistoryCache();
  if (cachedHistory !== null) {
    return cachedHistory;
  }

  const backendEntries = await fetchAllBackendRecentHistory(size, sort);
  const appStorageEntries = History.retrieveRecentHistory();
  const anonymousUpgradeEntries = HistoryHelper.getAnonymousRecentHistoryUpgradeSnapshot();
  const allEntries = HistoryHelper.mergeRecentHistoryEntries(backendEntries, [...anonymousUpgradeEntries, ...appStorageEntries], 'desc');
  const nonZeroEntries = allEntries.filter((entry) => {
    const resultCount = entry?.resultSize ?? entry?.totalResult ?? entry?.recordCount ?? entry?.resultsCount;
    return (resultCount == null || Number(resultCount) !== 0) && HistoryHelper.isRecentHistoryEntryWithinRetention(entry);
  });
  return HistoryHelper.setRecentHistoryCache(nonZeroEntries);
}

export const fetchRecentHistory = (page = 0, size = 15, sort = 'desc', query = '') =>
  logAsyncError(async (dispatch) => {
    dispatch(setHistoryLoading('recent'));
    try {
      const [allEntries, historyTypes] = await Promise.all([getCombinedRecentHistory(size, sort), HistoryResource.getHistoryTypes()]);
      const filteredEntries = HistoryHelper.filterRecentHistoryEntries(allEntries, query);
      const pageStart = page * size;
      const mergedTotalElements = filteredEntries.length;
      const mergedTotalPages = size > 0 ? Math.ceil(mergedTotalElements / size) : 0;
      const mergedContent = size > 0 ? filteredEntries.slice(pageStart, pageStart + size) : filteredEntries;

      dispatch(receivedRecentHistoryAction(mergedContent, historyTypes, mergedTotalElements, mergedTotalPages));

      if (query?.trim()) {
        trackHistoryMiniSearchResultsUpdated({
          page: 'recent',
          criteria: `qs=${query}`,
          totalResults: mergedTotalElements,
          currentPage: (page ?? 0) + 1,
          resultsPerPage: size,
          sortType: 'Relevance-desc',
          context: mergedContent?.[0]?.context,
          facets: [],
        });
      }
    } catch (e) {
      Logger.error(e);
      dispatch(receivedRecentHistoryAction([], undefined, 0, 0));
    }
  });
export const receivedSavedHistoryAction = (history, historyTypes) => {
  const formattedSavedHistory = HistoryHelper.formatSavedHistoryData([...history]);
  return {
    type: RECEIVED_SAVED_HISTORY,
    savedHistory: formattedSavedHistory || [],
    historyTypes,
  };
};
export const fetchSavedHistory = () =>
  logAsyncError(async (dispatch) => {
    dispatch(setHistoryLoading('saved'));
    const [history, historyTypes] = await Promise.all([HistoryResource.getSavedHistory(), HistoryResource.getHistoryTypes()]);
    dispatch(receivedSavedHistoryAction(history, historyTypes));
  });

export const handleViewSaveModal = (show, isRecentHistory, groupItemIndex = 0, itemIndex = 0, historyItem = {}) => ({
  type: HANDLE_VIEW_SAVE_MODAL,
  show,
  itemToBeSavedContext: historyItem.context ? historyItem.context : '',
  title: isRecentHistory ? 'Save' : 'Rename',
  historyItem,
  itemIndex,
  groupItemIndex,
});
export const openSaveModal = (props) => (dispatch) => {
  dispatch(
    updateDialog({
      id: typeOfHistorySaveDialog,
      dialogType: typeOfHistorySaveDialog,
    })
  );
  dispatch(handleViewSaveModal(true, true, 0, 0, props.historyItem));
};
export const dispatchOpenViewSaveModal = (...args) => {
  store.dispatch(openSaveModal(...args));
};
export const closeSaveModal = () => (dispatch) => {
  dispatch(
    closeDialog({
      id: typeOfHistorySaveDialog,
    })
  );
  dispatch(handleViewSaveModal(false));
};
export const closeConfirmationModal = () => (dispatch) => {
  dispatch(
    closeDialog({
      id: typeOfHistoryConfirmationDialog,
    })
  );
};
export const openConfirmationModal = (props) => (dispatch) => {
  dispatch(
    updateDialog({
      id: typeOfHistoryConfirmationDialog,
      dialogType: typeOfHistoryConfirmationDialog,
      backdrop: false,
      onClose: () => {
        dispatch(closeConfirmationModal());
      },
      ...props,
    })
  );
};
export const dispatchOpenConfirmationModal = (...args) => {
  store.dispatch(openConfirmationModal(...args));
};
export const handleNameChange = (value) => ({
  type: HANDLE_NAME_CHANGE,
  value,
});
export const onSubmitForm = (historyItem, name, isRename) => {
  // Legacy rows can carry a databaseId the backend no longer accepts, which fails the rename with
  // "database id {id} not found". Repair it before the write instead of surfacing the error.
  const historyItemWithName = { ...HistoryHelper.withResolvableDatabaseId(historyItem), searchName: name };
  return logAsyncError(async (dispatch) => {
    try {
      dispatch(onSubmitSave());
      // Saving a recent row re-executes the search first: its stored resultName may belong to a
      // previous session, and `history` save fails when the result set is no longer available.
      const shouldRerun = !isRename && Boolean(historyItem.resultSize || historyItem.totalResult);
      let historyItemToFormat = historyItemWithName;
      if (shouldRerun && historyItem.quickSearchItem) {
        historyItemToFormat = await History.rerunQuickSearchHistoryItem(historyItemWithName);
      } else if (shouldRerun && historyItem.queryFactItem) {
        historyItemToFormat = await History.rerunAdvancedSearchHistoryItem(historyItemWithName);
      }
      const historyItemToSave = isRename ? historyItemToFormat : await HistoryHelper.formatNewHistory(historyItemToFormat, name, true);
      const result = isRename
        ? await HistoryResource.modifyHistory(historyItemToSave)
        : await HistoryResource.saveHistory(historyItemToSave);
      dispatch(onSubmitSuccess(result, isRename, name, historyItem));
      dispatch(closeSaveModal());
    } catch (e) {
      Logger.error(e);
      dispatch(onSubmitFail(e));
      if (e?.statusCode === 401) {
        dispatch(closeSaveModal());
      }
    }
  });
};
export const handleViewDeleteModal = (show, groupItemIndex = 0, itemIndex = 0, historyItem = {}) => ({
  type: HANDLE_VIEW_DELETE_MODAL,
  show,
  historyItem,
  itemIndex,
  groupItemIndex,
});
export const onDelete = (historyItem) => {
  const historyItemToDelete = { ...HistoryHelper.withResolvableDatabaseId(historyItem) };
  return logAsyncError(async (dispatch) => {
    try {
      dispatch(onDeleteClick());
      const result = await HistoryResource.removeHistory(historyItemToDelete);
      dispatch(onDeleteSuccess(result));
      dispatch(handleViewDeleteModal(false));
    } catch (e) {
      Logger.error(e);
      dispatch(onDeleteFail(e));
      if (e?.statusCode === 401) {
        dispatch(handleViewDeleteModal(false));
      }
    }
  });
};

export const onPageChange = (pageSize, currentPage) => ({
  type: ON_PAGE_CHANGE,
  pageSize,
  currentPage,
});

function getRecentItems(historyItem) {
  return Array.isArray(historyItem) ? historyItem : [historyItem];
}

async function deleteRecentItems(recentItems, page) {
  const completedSaveIds = (await HistoryHelper.cancelPendingRecentHistorySaves(recentItems)) || [];
  let backendIds = HistoryHelper.getRecentHistoryBackendIds(recentItems) || [];
  // Backend-persisted rows displayed before their delayed save resolved may lack a local id;
  // resolve them against the backend list by merge identity. Session-only rows (incl. batch,
  // which is sessionStorage-only for now) never hit the backend.
  const shouldResolveBackendItems = recentItems.some((item) => typeof item !== 'string' && !item?.isSessionOnlyRecent && !item?.id);

  if (shouldResolveBackendItems) {
    const backendEntries = await fetchAllBackendRecentHistory(100, 'desc');
    backendIds = [...backendIds, ...(HistoryHelper.getRecentHistoryBackendIds(recentItems, backendEntries) || [])];
  }

  backendIds = [...new Set([...backendIds, ...completedSaveIds])];
  if (!backendIds.length) {
    return {};
  }
  return HistoryResource.deleteHistory(backendIds, page);
}

function getSafePositiveNumber(value, fallback) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : fallback;
}

export const onDeleteHistoryRecord = (historyItem, page) =>
  logAsyncError(async (dispatch, getState) => {
    try {
      dispatch(onDeleteClick());
      const isRecentPage = page?.toLowerCase() === 'recent';
      if (!isRecentPage) {
        const result = await HistoryResource.deleteHistory(historyItem, page);
        dispatch(onDeleteSuccess(result));
        dispatch(handleViewDeleteModal(false));
        dispatch(fetchSavedHistory());
        return;
      }

      const recentItems = getRecentItems(historyItem);
      const result = await deleteRecentItems(recentItems, page);
      const remainingRecentHistory = HistoryHelper.removeRecentHistoryEntries(recentItems);
      dispatch(onDeleteSuccess(result));
      dispatch(handleViewDeleteModal(false));

      const state = getState();
      const { currentPage, pageSize, searchInputText } = state.get(historyReducerKey);
      const safeCurrentPage = getSafePositiveNumber(currentPage, 1);
      const safePageSize = getSafePositiveNumber(pageSize, 15);
      const filteredEntries = HistoryHelper.filterRecentHistoryEntries(remainingRecentHistory, searchInputText || '');
      const lastPage = Math.max(Math.ceil(filteredEntries.length / safePageSize), 1);
      const nextCurrentPage = Math.min(safeCurrentPage, lastPage);
      if (nextCurrentPage !== safeCurrentPage) {
        dispatch(onPageChange(safePageSize, nextCurrentPage));
      }
      await dispatch(fetchRecentHistory(nextCurrentPage - 1, safePageSize, 'desc', searchInputText || ''));
    } catch (e) {
      Logger.error(e);
      dispatch(onDeleteFail(e));
      if (e?.statusCode === 401) {
        dispatch(handleViewDeleteModal(false));
      }
    }
  });

export const handleCheckboxChange = (resultName, isChecked) => ({
  type: HANDLE_CHECKBOX_CHANGE,
  resultName,
  isChecked,
});

export const toggleSelectAllOnPage = (resultNames, isSelected) => ({
  type: TOGGLE_SELECT_ALL_ON_PAGE,
  resultNames,
  isSelected,
});

export const handleSearchInput = (text) => ({
  type: HANDLE_SEARCH_INPUT,
  text,
});

History.registerSearchByQuery(Search.searchByQuery.bind(Search));
